import { Effect } from "effect"
import { clock, effect, frameLoop, sampler, surface, target } from "vgpu"
import type { Frame } from "vgpu"
import asciiShader from "../shaders/ascii.wgsl"
import sunShader from "../shaders/sun.wgsl"
import { readDay, sunAt } from "./day.ts"
import { rasterizeGlyphs } from "./glyphs.ts"
import { Gpu } from "./Gpu.ts"
import type { Scene } from "./Scene.ts"

const acquire = <A>(make: () => A, release: (a: A) => void) =>
  Effect.acquireRelease(Effect.sync(make), (a) => Effect.sync(() => release(a)))

/** Character cell in CSS pixels. */
const CELL_W = 9
const CELL_H = 16
/** Finer cells on narrow screens so the Sun keeps its detail. */
const cellFor = (cssWidth: number): [number, number] =>
  cssWidth < 640 ? [7, 12] : [CELL_W, CELL_H]
/** Atlas glyphs are rasterised at this multiple of the cell for crisp edges. */
const ATLAS_SCALE = 3
/** Render rate: full rate everywhere — a capped Sun visibly trails the page,
 * which scrolls at the display's native rate. Touch devices back off to 30
 * only if they can't hold the pace (thermals, low-power mode). */
const FPS = 60
const FPS_FLOOR = 30
/** Touch device? Phones get cheaper defaults; pointer-fine desktops are untouched. */
const isCoarse = () =>
  typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches

/** An ASCII Sun: pass 1 renders one texel per cell, pass 2 typesets it. */
export const sunScene: Scene = (canvas) =>
  Effect.gen(function* () {
    const gpu = yield* Gpu
    const coarse = isCoarse()

    const canvasSurface = yield* acquire(
      () => surface(gpu, canvas, { dpr: [1, 2] }),
      (s) => s.dispose(),
    )

    // --- glyph atlas -----------------------------------------------------------
    const glyphs = rasterizeGlyphs(CELL_W * ATLAS_SCALE, CELL_H * ATLAS_SCALE)
    const atlas = yield* acquire(
      () => {
        const tex = gpu.device.createTexture({
          label: "glyph-atlas",
          size: [glyphs.width, glyphs.height],
          format: "rgba8unorm",
          usage: ["texture_binding", "copy_dst", "render_attachment"],
        })
        gpu.gpu.queue.copyExternalImageToTexture({ source: glyphs.image }, { texture: tex.gpu }, [
          glyphs.width,
          glyphs.height,
        ])
        glyphs.image.close()
        return tex
      },
      (tex) => tex.dispose(),
    )

    const nearest = sampler(gpu, { magFilter: "nearest", minFilter: "nearest" })
    const linear = sampler(gpu, {
      magFilter: "linear",
      minFilter: "linear",
      addressModeU: "clamp-to-edge",
      addressModeV: "clamp-to-edge",
    })

    // --- passes ----------------------------------------------------------------
    const gridFor = (width: number, height: number): [number, number] => {
      const cssWidth = Math.max(canvas.clientWidth, 1)
      const dpr = width / cssWidth
      const [cw, ch] = cellFor(cssWidth)
      return [
        Math.max(1, Math.ceil(width / (cw * dpr))),
        Math.max(1, Math.ceil(height / (ch * dpr))),
      ]
    }
    let aspect = canvasSurface.size[0] / Math.max(canvasSurface.size[1], 1)
    const [gx, gy] = gridFor(canvasSurface.size[0], canvasSurface.size[1])
    // Owned by the device; released with it when the scope closes.
    const sunTarget = target(gpu, { label: "sun", size: [gx, gy], format: "rgba16float" })

    const sun = effect(gpu, sunShader, {
      label: "sun",
      set: {
        params: {
          time: 0,
          aspect,
          ...sunAt(0, aspect),
          pointer: sunAt(0, aspect).center,
        },
      },
    })
    const ascii = effect(gpu, asciiShader, {
      label: "ascii",
      set: {
        sun: sunTarget,
        atlas,
        nearest,
        linear,
        params: { grid: [gx, gy], glyphs: glyphs.count, time: 0 },
      },
    })

    yield* acquire(
      () =>
        canvasSurface.onResize(({ width, height }) => {
          const [w, h] = gridFor(width, height)
          sunTarget.resize([w, h])
          // Pixel aspect, not grid aspect — cells are not square.
          aspect = width / Math.max(height, 1)
          sun.set({ params: { aspect } })
          ascii.set({ sun: sunTarget, params: { grid: [w, h] } })
        }),
      (unsubscribe) => unsubscribe(),
    )

    // --- cursor ----------------------------------------------------------------
    // Target follows the pointer; the eased value is what the shaders see.
    // At rest the pointer sits on the Sun's centre, which disables the corona
    // reach (a resting pointer anywhere else would bias the streamers and make
    // the Sun look lopsided). Touch devices have no hovering cursor, so they
    // stay at rest permanently: a finger's last position lingering after a
    // scroll (pointercancel) was squashing the corona downward.
    const start = sunAt(readDay(), aspect).center
    const cursor = { x: start[0], y: start[1], tx: start[0], ty: start[1], active: false }
    // The hour of the day, eased so the Sun glides rather than tracks the scrollbar.
    let day = readDay()
    if (!coarse) {
      yield* acquire(
        () => {
          const onMove = (e: PointerEvent) => {
            const rect = canvas.getBoundingClientRect()
            cursor.tx = (e.clientX - rect.left) / Math.max(rect.width, 1)
            cursor.ty = (e.clientY - rect.top) / Math.max(rect.height, 1)
            cursor.active = true
          }
          const onLeave = () => {
            cursor.active = false
          }
          window.addEventListener("pointermove", onMove, { passive: true })
          window.addEventListener("pointercancel", onLeave)
          document.addEventListener("pointerleave", onLeave)
          window.addEventListener("blur", onLeave)
          return () => {
            window.removeEventListener("pointermove", onMove)
            window.removeEventListener("pointercancel", onLeave)
            document.removeEventListener("pointerleave", onLeave)
            window.removeEventListener("blur", onLeave)
          }
        },
        (remove) => remove(),
      )
    }

    // --- loop ------------------------------------------------------------------
    // 60 fps is plenty for a character grid; capped so GPU load stays reasonable.
    const time = clock(gpu)
    yield* acquire(
      () => {
        let fps = FPS
        let slow = 0
        let handle: { stop(): void }
        const tick = (frame: Frame) => {
          const dt = Math.min(time.deltaTime, 0.1)
          const k = 1 - Math.exp(-dt * 3.5)
          cursor.x += (cursor.tx - cursor.x) * k
          cursor.y += (cursor.ty - cursor.y) * k
          day += (readDay() - day) * (1 - Math.exp(-dt * 5))
          const { center, radius, dusk } = sunAt(day, aspect)
          if (!cursor.active) {
            cursor.tx = center[0]
            cursor.ty = center[1]
          }
          sun.set({
            params: { time: time.time, pointer: [cursor.x, cursor.y], center, radius, dusk },
          })
          ascii.set({ params: { time: time.time } })
          frame.pass(sunTarget, sun)
          frame.pass(canvasSurface, ascii)
          // A phone that keeps missing its budget settles at a lower, steadier rate.
          if (coarse && fps > FPS_FLOOR) {
            if (dt > 1.6 / fps) slow += 1
            else slow = Math.max(0, slow - 2)
            if (slow >= 45) {
              fps = FPS_FLOOR
              handle.stop()
              handle = frameLoop(gpu, tick, { fps })
            }
          }
        }
        handle = frameLoop(gpu, tick, { fps })
        return { stop: () => handle.stop() }
      },
      (loop) => loop.stop(),
    )

    return yield* Effect.never
  })
